import { calculateFileHash } from './imageOptimization';

interface UploadedFile {
  hash: string;
  url: string;
  name: string;
  size: number;
  uploadedAt: number;
}

/**
 * Duplicate Detection Manager
 * Stores file hashes in localStorage to detect duplicate uploads
 */
class DuplicateDetectionManager {
  private storageKey = 'edumi_uploaded_files';
  private maxEntries = 1000; // Keep last 1000 uploads

  /**
   * Get all uploaded files from storage
   */
  private getUploadedFiles(): UploadedFile[] {
    try {
      const data = localStorage.getItem(this.storageKey);
      return data ? JSON.parse(data) : [];
    } catch (error) {
      console.error('[Duplicate Detection] Error reading storage:', error);
      return [];
    }
  }

  /**
   * Save uploaded files to storage
   */
  private saveUploadedFiles(files: UploadedFile[]): void {
    try {
      // Keep only the most recent entries
      const trimmedFiles = files.slice(-this.maxEntries);
      localStorage.setItem(this.storageKey, JSON.stringify(trimmedFiles));
    } catch (error) {
      console.error('[Duplicate Detection] Error saving to storage:', error);
    }
  }

  /**
   * Check if file is a duplicate
   */
  async checkDuplicate(file: File): Promise<{
    isDuplicate: boolean;
    existingFile?: UploadedFile;
  }> {
    try {
      const hash = await calculateFileHash(file);
      const uploadedFiles = this.getUploadedFiles();
      
      const existingFile = uploadedFiles.find(f => f.hash === hash);
      
      if (existingFile) {
        console.log('[Duplicate Detection] Duplicate found:', {
          original: existingFile.name,
          new: file.name,
          hash: hash.substring(0, 16) + '...',
        });
        
        return {
          isDuplicate: true,
          existingFile,
        };
      }
      
      return { isDuplicate: false };
    } catch (error) {
      console.error('[Duplicate Detection] Error checking duplicate:', error);
      return { isDuplicate: false };
    }
  }

  /**
   * Register a newly uploaded file
   */
  async registerUpload(file: File, url: string): Promise<void> {
    try {
      const hash = await calculateFileHash(file);
      const uploadedFiles = this.getUploadedFiles();
      
      const newFile: UploadedFile = {
        hash,
        url,
        name: file.name,
        size: file.size,
        uploadedAt: Date.now(),
      };
      
      // Remove old entry with same hash if exists
      const filteredFiles = uploadedFiles.filter(f => f.hash !== hash);
      
      // Add new entry
      filteredFiles.push(newFile);
      
      this.saveUploadedFiles(filteredFiles);
      
      console.log('[Duplicate Detection] File registered:', {
        name: file.name,
        hash: hash.substring(0, 16) + '...',
        totalFiles: filteredFiles.length,
      });
    } catch (error) {
      console.error('[Duplicate Detection] Error registering upload:', error);
    }
  }

  /**
   * Clear old entries (older than specified days)
   */
  clearOldEntries(daysOld: number = 30): void {
    try {
      const uploadedFiles = this.getUploadedFiles();
      const cutoffTime = Date.now() - (daysOld * 24 * 60 * 60 * 1000);
      
      const recentFiles = uploadedFiles.filter(f => f.uploadedAt > cutoffTime);
      
      this.saveUploadedFiles(recentFiles);
      
      console.log('[Duplicate Detection] Cleared old entries:', {
        removed: uploadedFiles.length - recentFiles.length,
        remaining: recentFiles.length,
      });
    } catch (error) {
      console.error('[Duplicate Detection] Error clearing old entries:', error);
    }
  }

  /**
   * Clear all entries
   */
  clearAll(): void {
    try {
      localStorage.removeItem(this.storageKey);
      console.log('[Duplicate Detection] All entries cleared');
    } catch (error) {
      console.error('[Duplicate Detection] Error clearing all:', error);
    }
  }

  /**
   * Get statistics
   */
  getStats(): {
    totalFiles: number;
    totalSize: number;
    oldestUpload: number | null;
    newestUpload: number | null;
  } {
    const files = this.getUploadedFiles();
    
    if (files.length === 0) {
      return {
        totalFiles: 0,
        totalSize: 0,
        oldestUpload: null,
        newestUpload: null,
      };
    }
    
    return {
      totalFiles: files.length,
      totalSize: files.reduce((sum, f) => sum + f.size, 0),
      oldestUpload: Math.min(...files.map(f => f.uploadedAt)),
      newestUpload: Math.max(...files.map(f => f.uploadedAt)),
    };
  }
}

// Export singleton instance
export const duplicateDetection = new DuplicateDetectionManager();

/**
 * Check if file is duplicate and show confirmation
 */
export async function checkAndConfirmDuplicate(file: File): Promise<{
  shouldUpload: boolean;
  existingUrl?: string;
}> {
  const result = await duplicateDetection.checkDuplicate(file);
  
  if (result.isDuplicate && result.existingFile) {
    const confirmed = window.confirm(
      `ไฟล์นี้เคยอัปโหลดไปแล้วเมื่อ ${new Date(result.existingFile.uploadedAt).toLocaleString('th-TH')}\n\n` +
      `ชื่อไฟล์เดิม: ${result.existingFile.name}\n` +
      `ขนาด: ${(result.existingFile.size / 1024 / 1024).toFixed(2)} MB\n\n` +
      `คุณต้องการใช้ไฟล์เดิมหรืออัปโหลดใหม่?\n\n` +
      `กด OK เพื่อใช้ไฟล์เดิม (ประหยัดพื้นที่)\n` +
      `กด Cancel เพื่ออัปโหลดใหม่`
    );
    
    if (confirmed) {
      return {
        shouldUpload: false,
        existingUrl: result.existingFile.url,
      };
    }
  }
  
  return { shouldUpload: true };
}
